<template>
  <BGModal :shown="dialogShown"
           :title="`${$t('admin_session.edit_title')} ${session ? '(' + $sanitizeHtml(session.name) + ')' : ''}`">
    <template #content>
      <v-container class="edit-session-content" fluid>
        <v-form v-model="isFormValid"
                class="flex-grow-1"
                ref="sessionForm">
          <v-row>
            <v-col>
              <v-text-field readonly
                            :label="$t('admin_hkiarace.event')"
                            :value="$sanitizeHtml(session.event.name)"></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field readonly
                            :label="$t('admin_event.signup_mode')"
                            :value="signupModeLabel"></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field outlined
                            :label="$t('admin_session.name_en')"
                            v-model="session.name_en"
                            :rules="requiredRules"></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field outlined
                            :label="$t('admin_session.name_zh')"
                            v-model="session.name_zh"
                            :rules="requiredRules"></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field outlined
                            :label="$t('admin_event.order_no')"
                            v-model="session.order_no"
                            type="number"
                            min="0"
                            step="1"
                            :rules="requiredRules"></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-input :rules="requiredRules"
                       v-model="session.start">
                <v-container fluid class="flex-column pa-0">
                  <legend class="v-label theme--light">{{ $t('admin_session.start') }}</legend>
                  <DateTimePickerInput v-model="session.start"
                                       hide-details></DateTimePickerInput>
                </v-container>
              </v-input>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-input :rules="sessionEndRules"
                       v-model="session.end">
                <v-container fluid class="flex-column pa-0">
                  <legend class="v-label theme--light">{{ $t('admin_session.end') }}</legend>
                  <DateTimePickerInput v-model="session.end"
                                       hide-details></DateTimePickerInput>
                </v-container>
              </v-input>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-input :rules="[
                      value => (unlimitedQuota || !!value) || $t('register.required_fields'),
                    ]"
                       v-model="session.quota"
                       v-if="session.event.signup_mode === 2">
                <v-container fluid class="px-0">
                  <v-row>
                    <v-col class="pb-0">
                      <v-text-field outlined
                                    :label="$t('admin_session.quota')"
                                    type="number"
                                    v-model="session.quota"
                                    hide-details
                                    :disabled="unlimitedQuota">
                      </v-text-field>
                    </v-col>
                    <v-col cols="2" class="pb-0">
                      <v-checkbox :label="$t('unlimited')"
                                  v-model="unlimitedQuota"></v-checkbox>
                    </v-col>
                  </v-row>
                </v-container>
              </v-input>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
    </template>

    <template #footer>
      <v-row no-gutters>
        <v-btn @click="cancel"
               class="mr-3"
               depressed
               large>
          {{ $t('cancel') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary"
               depressed
               large
               @click="$refs.sessionForm.validate() && save()">
          {{ $t('save') }}
          <v-icon right>fas fa-check</v-icon>
        </v-btn>
      </v-row>
    </template>
  </BGModal>
</template>
<script>
import BGModal from "@/components/BGModal";
import {computed, ref, watch} from "@vue/composition-api";
import DateTimePickerInput from "@/components/DateTimePickerInput";

export default {
  name: 'EditSessionModal',
  components: {DateTimePickerInput, BGModal},
  props: {
    shown: Boolean,
    sessionId: String,
  },
  setup(props, {root, emit}) {
    const dialogShown = ref(props.shown);

    const tabIndex = ref(0);

    const requiredRules = ref([
      value => !!value || root.$t('register.required_fields'),
    ]);

    // Init
    watch(() => props.shown, async (newValue, oldValue) => {
      if (newValue !== oldValue) {
        dialogShown.value = newValue;

        if (newValue) {
          tabIndex.value = 0;
          getSession();
        }
      }

      tabIndex.value = 0;
    });
    const cancel = function () {
      emit('cancel');
    };

    const isFormValid = ref(false);
    const session = ref(null);
    const getSession = function () {
      root.$callAPI('event/session/', {session_id: props.sessionId}, (response) => {
        session.value = response.body.session;
        unlimitedQuota.value = session.quota === -1;
      })
    };
    const signupModeLabel = computed(() => {
      if (session.value) {
        return {
          "-1": root.$t('event_signup_mode.disabled'),
          "1": root.$t('event_signup_mode.single_session'),
          "2": root.$t('event_signup_mode.multi_session'),
          "3": root.$t('event_signup_mode.multi_series'),
        }[session.value.event.signup_mode];
      }
    })
    const sessionEndRules = ref([
      ...requiredRules.value,
      value => (!session.value.start || value > session.value.start) || 'Please enter valid session start / end',
    ])
    const unlimitedQuota = ref(false);

    const save = function () {
      let params = {
        ...session.value,
        session_id: props.sessionId,
        quota: (session.value.event.signup_mode === 2 && !unlimitedQuota.value) ? session.value.quota : -1,
      }

      root.$callAPI('event/session/edit/', params, (response) => {
        emit('finish', response.body.session);
      });
    }

    return {
      dialogShown,
      tabIndex,
      cancel,

      isFormValid,
      requiredRules,

      signupModeLabel,

      session,
      sessionEndRules,
      unlimitedQuota,

      save,
    }
  },
}
</script>

<style lang="less">
.edit-session-content {
  display: flex;
  height: 100%;
}
</style>
